import api from "@src/api/api";
import PopupWrapper from "@src/components/elements/PopupWrapper";
import UserWidget from "@src/components/elements/UserWidget";
import Button from "@src/components/elements/input/Button";
import TabFilter from "@src/components/menu/TabFilter";
import { getChatPartner } from "@src/pages/messenger/Messenger";
import { useAppSelector } from "@src/state/hooks";
import getName from "@src/utils/getName";
import { ChatType } from "@src/utils/types/ChatType";
import { Media } from "@src/utils/types/Media";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import tw from "twin.macro";

type Props = {
  onClose: () => void;
  message: {
    text: string;
    link: string | undefined;
    media?: Media[];
  };
};

const ForwardMessagePopup = ({ onClose, message }: Props) => {
  const [chatFilter, setChatFilter] = useState("");
  const [selectedChats, setSelectedChats] = useState<string[]>([]);
  const { user } = useAppSelector(state => state.user);
  const queryClient = useQueryClient();

  const chats = useQuery<ChatType[], Error>(["chats", chatFilter], async () => {
    const { data } = await api.get("/api/v1/chats/" + chatFilter);
    // data[0] && setSelectedChat(data[0].id);
    return data;
  });

  const sendMessage = useMutation(["send-message"], {
    mutationFn: async () => {
      for (const selectedChat of selectedChats) {
        const mediaIds = message.media?.map(m => m.id);
        await api.post(`/api/v1/chats/${selectedChat}/messages`, {
          text: message.text,
          link: message.link,
          media: mediaIds,
        });
        queryClient.invalidateQueries({
          queryKey: ["messages-" + selectedChat],
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chats"],
      });
      queryClient.invalidateQueries({
        queryKey: ["messages"],
      });
      onClose();
    },
    onError: () => {
      alert("Failed");
    },
  });

  if (!user) return <></>;
  if (!chats.isSuccess) return <></>;
  return (
    <PopupWrapper onClose={onClose}>
      <div className="no-scrollbar flex flex-col gap-2">
        <TabFilter
          options={[
            { name: "Chats", key: "" },
            { name: "Archiviert", key: "archive" },
            { name: "Gruppen", key: "groups" },
          ]}
          value={chatFilter}
          onChange={setChatFilter}
          subTab={true}
        />
        {chats.data.map(chat => (
          <button
            key={chat.id}
            className="group relative flex flex-row gap-2 rounded-2xl bg-gray-200 p-2"
            onClick={() =>
              setSelectedChats(data => {
                if (data.includes(chat.id)) {
                  return data.filter(id => id !== chat.id);
                } else {
                  return [...data, chat.id];
                }
              })
            }
          >
            <div css={[tw`items-center justify-center h-[45px] flex`]}>
              <input
                id="chat-checkbox"
                name="chat-checkbox"
                type="checkbox"
                checked={selectedChats.includes(chat.id)}
                className="size-4 rounded-full border-gray-300 text-blue-500 focus:ring-offset-0"
              />
            </div>
            <div className="my-auto">
              <UserWidget
                user={getChatPartner(chat.members, user)}
                follow={false}
                online={false}
                size={45}
              />
            </div>
            <div className="flex flex-col">
              <div className="text-left font-semibold">
                {getName(getChatPartner(chat.members, user))}
              </div>
              <div className="line-clamp-1 text-left text-xs">
                {chat.last_message_text.startsWith("chat/media/")
                  ? "Media"
                  : chat.last_message_text}
              </div>
              <div className="text-left text-xs">{chat.created_at}</div>
            </div>
          </button>
        ))}
        <Button onClick={() => sendMessage.mutate()}>Senden</Button>
      </div>
    </PopupWrapper>
  );
};

export default ForwardMessagePopup;
